import { Check, Clock } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { money, type OfferPackage } from '@/types/offer';

export default function PackageCard({
  pkg,
  currency,
  chosen = false,
  onChoose,
  testId,
}: {
  pkg: OfferPackage;
  currency: string;
  chosen?: boolean;
  onChoose?: () => void;
  testId?: string;
}) {
  return (
    <Card
      className={`${chosen ? 'border-primary ring-1 ring-primary' : ''} ${onChoose ? 'cursor-pointer transition-colors hover:border-primary/60' : ''}`}
      onClick={onChoose}
      data-testid={testId}
    >
      <CardContent className="space-y-3 pt-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold">{pkg.name}</p>
            {pkg.summary && (
              <p className="mt-0.5 text-sm text-muted-foreground">{pkg.summary}</p>
            )}
          </div>
          {chosen && <Badge>Chosen</Badge>}
        </div>

        <p className="text-2xl font-bold tracking-tight">
          {money(pkg.monthly_fee, currency)}
          <span className="ml-1 text-sm font-normal text-muted-foreground">
            per month
          </span>
        </p>

        {pkg.services.length > 0 && (
          <ul className="space-y-1.5">
            {pkg.services.map((service, index) => (
              <li key={service.id ?? index} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                <span>{service.text}</span>
              </li>
            ))}
          </ul>
        )}

        {pkg.hourly_rates.length > 0 && (
          <div className="space-y-1 border-t pt-3">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              Hourly rates outside the package
            </p>
            {pkg.hourly_rates.map((rate, index) => (
              <div
                key={rate.id ?? index}
                className="flex justify-between text-sm"
              >
                <span className="text-muted-foreground">{rate.label}</span>
                <span className="font-medium">
                  {money(rate.hourly_rate, currency)} / hour
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
